import { Component, OnInit, Inject } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ComponentType } from '@angular/cdk/portal';
import { Observable } from 'rxjs';
import { isArray } from 'util';
import { DialogComponent } from '@shared/components/dialog/dialog.component';
import { OptionsJsonBuilder } from '@shared/components/json-builder/json-builder.service';
import { IButtonAddAttribute } from '@shared/components/json-builder/templates-json/template-button-add-attribute/template-button-add-attribute.component';
import { DialogObjectComponentToken } from '../dialog-object/dialog-object-token.component';
import { DialogArrayComponentToken } from './dialog-array-token.component';
import { DialogPropertyComponentToken } from '../dialog-property/dialog-property-token.component';

@Component({
  templateUrl: './dialog-array.component.html',
  styleUrls: ['./dialog-array.component.scss']
})

export class DialogArrayComponent implements OnInit {
  public form: FormGroup;
  public hasItems: boolean = false;
  public fieldsAutoComplete: Observable<OptionsJsonBuilder[]>;
  private usedKeys = [];
  private data: any;
  private root;
  private rootType;
  public configHeader = {
    hidden: true
  }
  public configButtonAddAttribute: IButtonAddAttribute = {
    withoutItemMessage: "Array vazio, adicione itens",
    btnAddMessage: "Adicionar item",
    hiddenWithoutItemMessage: this.hasItems
  }

  public breadcrumb;
  constructor(
    private dialog: MatDialog,
    private dialogRef: MatDialogRef<DialogArrayComponent>,
    private dataComponent: DialogComponent,
    private formBuilder: FormBuilder,
    @Inject(DialogObjectComponentToken) private dialogObjectComponentToken: ComponentType<any>,
    @Inject(DialogArrayComponentToken) private dialogArrayComponentToken: ComponentType<any>,
    @Inject(DialogPropertyComponentToken) private dialogPropertyComponentToken: ComponentType<any>
  ) {
    this.form = this.formBuilder.group({
      name: ['', [Validators.required]],
      value: [[], Validators.required]
    });
  }

  ngOnInit() {
    this.init();
    this.handleFieldName();
    this.setValueOnForm();
  }

  private init() {
    this.data = JSON.parse(JSON.stringify(this.dataComponent.data.data));
    this.root = this.dataComponent.data.data.root;

    if (isArray(this.root)) {
      this.rootType = 'array';
    } else {
      this.rootType = 'object';
      this.usedKeys = Object.keys(this.root);
    }
    this.breadcrumb = this.dataComponent.data.breadcrumb;
    delete this.data.root;
  }

  private setValueOnForm() {
    const name = this.data.name;
    const value = [];
    value.push(JSON.parse(JSON.stringify(this.data.data)));
    this.form.valueChanges.subscribe(() => {
      this.checkHasItem();
    });
    this.form.patchValue({ name, value });
  }

  private handleFieldName() {
    if (this.rootType === 'object') {
      this.setCustomValidatorName();
    } else {
      this.f.name.disable();
    }
  }

  private setCustomValidatorName() {
    this.f.name.setValidators([Validators.required, validatorKey(this.usedKeys, this.data.name)]);
    this.f.name.updateValueAndValidity();
  }

  private checkHasItem() {
    this.hasItems = !!this.f.value.value[0].length;
    this.configButtonAddAttribute.hiddenWithoutItemMessage = this.hasItems;
  }

  public addAttribute(type: 'object' | 'array' | 'item') {
    switch (type) {
      case 'object':
        return this.addObject();
      case 'array':
        return this.addArray();
      case 'item':
        return this.addItem();
    }
  }

  private addObject() {
    const breadcrumb = JSON.parse(JSON.stringify(this.breadcrumb));
    breadcrumb.whereThis.push({ title: this.f.name.value ?? 'Array'})
    this.dialog.open(DialogComponent, {
      width: '1080px',
      minHeight: '600px',
      disableClose: true,
      data: {
        titleDialog: 'Objeto',
        titleButton: 'Salvar',
        component: this.dialogObjectComponentToken,
        contentDialogClassName: 'dialog-content',
        data: {
          data: {},
          root: this.f.value.value[0]
        },
        breadcrumb
      },
      autoFocus: false
    }).afterClosed().subscribe(() => {
      this.f.value.updateValueAndValidity();
    })
  }

  private addArray() {
    const breadcrumb = JSON.parse(JSON.stringify(this.breadcrumb));
    breadcrumb.whereThis.push({ title: this.f.name.value ?? 'Array'})
    this.dialog.open(DialogComponent, {
      width: '1080px',
      minHeight: '600px',
      disableClose: true,
      data: {
        titleDialog: 'Array',
        titleButton: 'Salvar',
        component: this.dialogArrayComponentToken,
        contentDialogClassName: 'dialog-content',
        data: {
          data: [],
          root: this.f.value.value[0]
        },
        breadcrumb
      },
      autoFocus: false
    }).afterClosed().subscribe(() => {
      this.f.value.updateValueAndValidity();
    })
  }

  private addItem() {
    const breadcrumb = JSON.parse(JSON.stringify(this.breadcrumb));
    breadcrumb.whereThis.push({ title: this.f.name.value ?? 'Array'})
    this.dialog.open(DialogComponent, {
      width: '1080px',
      minHeight: '600px',
      disableClose: true,
      data: {
        titleDialog: 'Valor',
        titleButton: 'Salvar',
        component: this.dialogPropertyComponentToken,
        contentDialogClassName: 'dialog-content',
        data: {
          data: "",
          root: this.f.value.value[0]
        },
        breadcrumb
      },
      autoFocus: false
    }).afterClosed().subscribe(() => {
      this.f.value.updateValueAndValidity();
    })
  }

  public deleteAllEvent() {
    this.form.patchValue({ value: [[]] });
  }

  onSubmit() {
    const data = {
      index: this.data.index,
      name: this.f.name.value,
      value: this.f.value.value[0]
    }

    if (this.rootType === 'object') {
      this.root[data.name] = data.value;
      if (data.name !== this.data.name) {
        delete this.root[this.data.index];
      }
    } else {
      if (data.index === undefined) {
        this.root.push(data.value)
      } else {
        this.root[data.index] = data.value;
      }
    }

    this.dialogRef.close();
  }

  get f() {
    return this.form.controls;
  }
}

function validatorKey(usedKeys, initialKeyName) {
  return (control: AbstractControl) => {
    if (control.value && control.value !== "") {
      if (usedKeys.includes(control.value) && control.value !== initialKeyName) {
        return { keyAlreadyExists: true }
      }
    }
    return null;
  }
}
